import { useCallback, useContext } from "react";
import { ShellContext } from "../context";
import { useOwnerModule } from "./useOwnerModule";
import { ModuleInfo } from "../model";

/**
 * The path resolve function.
 */
export interface PathResolveFunction {
  /**
   * Returns a path relative to the root of the specified module.
   * @param module The module to return the relative path to.
   * @param path The relative path.
   */
  (module: ModuleInfo, path: string): string;

  /**
   * Returns a path relative to the root of the owning module. If no owning module exists, the original path is returned.
   * @param path The relative path.
   */
  (path: string): string;
}

/**
 * Gets a module path resolver function namespaced to the current owning module.
 */
export function useModulePathResolver(): PathResolveFunction {
  let { moduleRootPath } = useContext(ShellContext);

  const ownerModule = useOwnerModule();

  return useCallback(
    (...args: [ModuleInfo | string, string?]): string => {
      let module: ModuleInfo | null;
      let path: string;

      if (args.length === 2) {
        module = args[0] as ModuleInfo;
        path = args[1] as string;
      } else {
        module = ownerModule;
        path = args[0] as string;
      }

      let finalPath = path;
      if (!path.startsWith("/")) finalPath = `/${finalPath}`;
      if (finalPath === "/") finalPath = "";
      if (moduleRootPath === "/") moduleRootPath = "";

      return module ? `${moduleRootPath}/${module.id}${finalPath}` : finalPath;
    },
    [moduleRootPath],
  );
}
